import { Component } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import * as option from "@/models/configurator/configurator_options";
import {
  LayoutStore,
  ShapeStore,
  CanopyStore,
  FrameStore
} from "@/mixins/store";
import { StockMixin } from "@/mixins/stock";
import { FabricMixin } from "@/mixins/fabric";
import { UmbrellaModel } from "@/models/products/umbrella";
import { ProductLineName } from "@/models/products/collection";
import {
  Finish,
  FinishDisplayName,
  FinishHandle
} from "@/models/products/finish";

/**
 * Alta Pagoda and Estrella canopy styles (FUMBSD-693).
 *
 *                      Alta Pagoda              Estrella     Both
 * Air vents            off                      off          off
 * Valance              allowed                  off          off
 * Fringe, underside    allowed                  off          off
 * Designer trim        allowed                  allowed      allowed
 * Finial               Alta Pagoda Stainless    selectable   Alta Pagoda Stainless
 *
 * Availability comes from the umbrella model's `attributes.alta_pagoda` and
 * `attributes.estrella` flags. Both styles are custom only.
 */

export const ALTA_PAGODA_FINIAL_LABEL = "Alta Pagoda Stainless";

export const supportsAltaPagoda = (model: UmbrellaModel | null): boolean =>
  !!model && !!model.attributes && model.attributes.alta_pagoda === true;

export const supportsEstrella = (model: UmbrellaModel | null): boolean =>
  !!model && !!model.attributes && model.attributes.estrella === true;

export const canopyStyleLabel = (
  altaPagoda: boolean,
  estrella: boolean
): string | null => {
  if (altaPagoda && estrella) return "Alta Pagoda & Estrella";
  if (altaPagoda) return "Alta Pagoda";
  if (estrella) return "Estrella";
  return null;
};

/**
 * "Single" or "Split" panels, shown beside the style. Every Estrella is split
 * into 16 panels; an Alta Pagoda is 8 unless it is split. Null on a standard
 * canopy, which shows neither line.
 */
export const canopyPanelsLabel = (
  altaPagoda: boolean,
  estrella: boolean,
  splitPanel: boolean
): string | null => {
  if (!altaPagoda && !estrella) return null;
  return estrella || splitPanel ? "Split" : "Single";
};

/** The finish recorded with the Alta Pagoda kit's finial. */
export const ALTA_PAGODA_FINIAL_FINISH: Finish = {
  handle: FinishHandle.SS,
  display_name: FinishDisplayName.SS
};

/**
 * The finial a line starts with, restored when the Alta Pagoda kit comes off.
 * Matches the defaults the Node API uses for a SKU-parsed config: Greenwich
 * lines take a chrome vertex, Monterey G-Series a chrome ball, and the other
 * Monterey lines a ball matched to the frame finish.
 */
export const defaultFinialFor = (
  productLine: string,
  frameFinish: Finish | null
): { finial: option.Finial | null; finialFinish: Finish | undefined } => {
  const chrome: Finish = {
    handle: FinishHandle.CH,
    display_name: FinishDisplayName.CH
  };
  switch (productLine) {
    case ProductLineName.Greenwich:
    case ProductLineName.GreenwichGSeries:
      return { finial: option.Finial.Vertex, finialFinish: chrome };
    case ProductLineName.MontereyGSeries:
      return { finial: option.Finial.Ball, finialFinish: chrome };
    case ProductLineName.MontereyPulleyLift:
    case ProductLineName.MontereyAutoTilt:
    case ProductLineName.MontereyCrank:
      return {
        finial: option.Finial.Ball,
        finialFinish: frameFinish ?? undefined
      };
    default:
      return { finial: null, finialFinish: undefined };
  }
};

/**
 * Sends the canopy style to the viewer. Guarded so the app keeps working
 * against a viewer build that predates these commands. Split panels only
 * exist on a styled canopy; the viewer rejects them on a standard one.
 */
export const sendCanopyVariant = async (
  viewer: ViewerWindow | undefined,
  altaPagoda: boolean,
  estrella: boolean,
  splitPanel: boolean
): Promise<void> => {
  if (!viewer) return;
  if (typeof viewer.ChangeCanopyVariant === "function") {
    await viewer.ChangeCanopyVariant(altaPagoda, estrella);
  }
  if (typeof viewer.SetCanopySplitPanel === "function") {
    await viewer.SetCanopySplitPanel((altaPagoda || estrella) && splitPanel);
  }
};

@Component
export class AltaPagodaEstrellaRules extends mixins(
  LayoutStore,
  ShapeStore,
  CanopyStore,
  FrameStore,
  StockMixin,
  FabricMixin
) {
  /** Whether the model offers the style at all; it stays visible but disabled in stock mode. */
  get altaPagodaOffered(): boolean {
    return supportsAltaPagoda(this.umbrellaModel);
  }

  get estrellaOffered(): boolean {
    return supportsEstrella(this.umbrellaModel);
  }

  get altaPagodaAvailable(): boolean {
    return !this.isStockOnly && this.altaPagodaOffered;
  }

  get estrellaAvailable(): boolean {
    return !this.isStockOnly && this.estrellaOffered;
  }

  get hasCanopyStyle(): boolean {
    return this.altaPagoda || this.estrella;
  }

  /** Fringe and underside fabric are not offered on an Estrella canopy. */
  get canopyAccentsBlockedByStyle(): boolean {
    return this.estrella;
  }

  get finialLockedByStyle(): boolean {
    return this.altaPagoda;
  }

  /** Alta Pagoda and Estrella canopies attach with grommets only; pockets are never offered. */
  get grommetOnlyByStyle(): boolean {
    return this.hasCanopyStyle;
  }

  protected async changeViewerCanopyVariant(): Promise<void> {
    await sendCanopyVariant(
      this.$viewer,
      this.altaPagoda,
      this.estrella,
      this.splitPanel
    );
  }

  /** Drops whichever style the current model or stock mode no longer allows. */
  protected async dropUnavailableCanopyStyles(): Promise<void> {
    if (this.altaPagoda && !this.altaPagodaAvailable) {
      await this.addAltaPagoda(false);
    }
    if (this.estrella && !this.estrellaAvailable) {
      await this.addEstrella(false);
    }
  }

  /**
   * Brings the stored configuration and the viewer in line with the selected
   * canopy style. Vent and valance availability is handled alongside the model
   * attributes in attribute_rules. The viewer is absent while a saved design is
   * still loading; StartApp picks the stored values up from there.
   */
  /**
   * The Alta Pagoda kit replaces the finial outright, so the store records it
   * as finial "alta_pagoda" with a stainless finish rather than keeping the last
   * selection. Taking the kit off puts the line's default finial back.
   * The viewer reads these from the store; ChangeFinial is not called with the kit on.
   */
  protected async applyAltaPagodaFinial(): Promise<void> {
    if (this.altaPagoda) {
      if (this.finial !== option.Finial.AltaPagoda) {
        await this.addFinial(option.Finial.AltaPagoda);
        await this.addFinialFinish(ALTA_PAGODA_FINIAL_FINISH);
      }
      return;
    }
    if (this.finial === option.Finial.AltaPagoda) {
      const restored = defaultFinialFor(
        this.collection ? this.collection.handle : "",
        this.finish
      );
      await this.addFinial(restored.finial as option.Finial);
      await this.addFinialFinish(restored.finialFinish as Finish);
      if (this.$viewer && restored.finial && restored.finialFinish) {
        await this.$viewer.ChangeFinial(
          restored.finial,
          restored.finialFinish.handle
        );
      }
    }
  }

  protected async applyCanopyStyleRules(): Promise<void> {
    if (!this.hasCanopyStyle) return;

    if (this.preset === option.Preset.S1DV || this.ventMiddle) {
      await this.addVentMiddleFabric([]);
      await this.addVentMiddle(null);
      if (this.$viewer) await this.$viewer.ChangeVent("single");
      if (this.preset === option.Preset.S1DV) {
        await this.addPreset(option.Preset.S1);
        if (this.$viewer) await this.$viewer.ChangeCanopyLayout(option.Preset.S1);
      }
    }

    if (this.grommetOnlyByStyle && this.rib !== option.Rib.Grommets) {
      await this.addRib(option.Rib.Grommets);
      await this.addRibFabric(null);
      if (this.$viewer) await this.$viewer.ChangeRibAttachment(option.Rib.Grommets);
    }

    if (this.canopyAccentsBlockedByStyle) {
      if (this.fringe && this.fringe !== option.Fringe.None) {
        await this.addFringe(option.Fringe.None);
        await this.addFringeFabric([]);
        if (this.$viewer) await this.$viewer.ChangeFringe(null);
      }
      if (this.undersideFabric) {
        await this.addUndersideFabric(null);
        if (this.$viewer) await this.$viewer.ChangeUndersideFabric(null);
      }
    }
  }
}
